/**
 * Do not disturb (DS v9, boards Dnd-*): how long it lasts and how the end time reads.
 * Kept in step with MobileCh3 `src/screens/dnd/dndModel.ts` so both apps set and show the same times:
 * "Until tomorrow" and "Until next week" end at 9:00 the next day / next Monday.
 */
import moment from "moment-timezone";

export type DndOption = 'min30' | 'hour1' | 'tomorrow' | 'nextWeek' | 'custom' | 'untilOff';

export const DND_OPTIONS: { value: DndOption; label: string }[] = [
    {value: 'min30', label: 'For 30 minutes'},
    {value: 'hour1', label: 'For 1 hour'},
    {value: 'tomorrow', label: 'Until tomorrow'},
    {value: 'nextWeek', label: 'Until next week'},
    {value: 'custom', label: 'Choose date and time'},
    {value: 'untilOff', label: 'Until turned off'},
];

const nineAm = (m: moment.Moment) => m.hours(9).minutes(0).seconds(0).milliseconds(0);

/** When DND set with `option` at `now` ends. `null` means until turned off; `custom` uses `customAt`. */
export const dndDueDate = (option: DndOption, now: Date, customAt?: Date): Date | null => {
    const m = moment(now);
    switch (option) {
        case 'min30':
            return m.add(30, 'minutes').toDate();
        case 'hour1':
            return m.add(1, 'hour').toDate();
        case 'tomorrow':
            return nineAm(m.add(1, 'day')).toDate();
        case 'nextWeek':
            // The Monday after `now` (a week ahead when today is Monday), like date-fns `nextMonday`.
            return nineAm(m.add(1, 'week').startOf('isoWeek')).toDate();
        case 'custom':
            return customAt ?? null;
        case 'untilOff':
            return null;
    }
};

/** Wire format the DND endpoints already use: local time with offset. */
export const toDndPayload = (date: Date | null): string | null =>
    date ? moment(date).format("YYYY-MM-DDTHH:mm:ss.SSSZ") : null;

/** The first quarter hour at least 15 minutes after `now`: the default for "Choose date and time". */
export const nextQuarterHour = (now: Date): Date => {
    const m = moment(now).add(15, 'minutes').seconds(0).milliseconds(0);
    const rest = m.minutes() % 15;
    if (rest) m.add(15 - rest, 'minutes');
    return m.toDate();
};

export const formatTime = (date: Date) => moment(date).format('HH:mm');
export const formatDate = (date: Date) => moment(date).format('D MMM YYYY');

/** Parses a DND due date from the API; empty or invalid values mean "until turned off". */
export const parseDue = (value?: string | null): Date | null => {
    if (!value) return null;
    const m = moment(value);
    return m.isValid() ? m.toDate() : null;
};

/**
 * On, and not past its end. The backend keeps `enabled` after the due date passes,
 * so an expired DND must read as off.
 */
export const isDndOn = (dnd: { enabled?: boolean; dueDate?: string | null } | null | undefined, now = new Date()): boolean => {
    if (dnd?.enabled !== true) return false;
    const due = parseDue(dnd.dueDate);
    return !due || due > now;
};

/** "17:15" today, "tomorrow at 9:00", "Mon 27 Oct at 9:00" after that. */
export const dndUntil = (due: Date, now = new Date()): string => {
    const d = moment(due);
    const time = d.format('H:mm');
    if (d.isSame(now, 'day')) return time;
    if (d.isSame(moment(now).add(1, 'day'), 'day')) return `tomorrow at ${time}`;
    return `${d.format('ddd D MMM')} at ${time}`;
};

/** "until 17:15 today", "until tomorrow at 9:00", "until you turn it off". */
export const silencedUntil = (due: Date | null, now = new Date()): string => {
    if (!due) return 'until you turn it off';
    return moment(due).isSame(now, 'day') ? `until ${dndUntil(due, now)} today` : `until ${dndUntil(due, now)}`;
};

/** Short value for a menu row: "Off", "Until 17:15", "Until turned off". */
export const dndShortValue = (dnd: { enabled?: boolean; dueDate?: string | null } | null | undefined, now = new Date()): string => {
    if (!isDndOn(dnd, now)) return 'Off';
    const due = parseDue(dnd?.dueDate);
    return due ? `Until ${dndUntil(due, now)}` : 'Until turned off';
};
